/// <reference types="../../../../../../env.d.ts" />

import { Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env['_NGX_ENV_FIREBASE_API_KEY'],
  authDomain: import.meta.env['_NGX_ENV_FIREBASE_AUTH_DOMAIN'],
  projectId: import.meta.env['_NGX_ENV_FIREBASE_PROJECT_ID'],
  storageBucket: import.meta.env['_NGX_ENV_FIREBASE_STORAGE_BUCKET'],
  messagingSenderId: import.meta.env['_NGX_ENV_FIREBASE_MESSAGING_SENDER_ID'],
  appId: import.meta.env['_NGX_ENV_FIREBASE_APP_ID'],
  measurementId: import.meta.env['_NGX_ENV_FIREBASE_MEASUREMENT_ID'],
};

// Detect if we're in development mode - be defensive about undefined env vars
const isDevelopment = false; // Disable emulators for production deployment

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  // Consolidated single-project architecture
  app = initializeApp(firebaseConfig, 'app');
  auth = getAuth(this.app);
  db = this.initializeFirestore(); // Initialize Firestore with optional custom database ID
  functions = getFunctions(this.app, 'us-west4');
  storage = getStorage(this.app);

  constructor() {
    if (isDevelopment) {
      this.connectToEmulators();
    }
  }

  /**
   * Initialize Firestore with optional custom database ID
   */
  private initializeFirestore() {
    const databaseId = this.getDatabaseId();
    if (databaseId) {
      console.log(`🔧 Using custom Firestore database: ${databaseId}`);
      return getFirestore(this.app, databaseId);
    } else {
      console.log('🔧 Using default Firestore database: (default)');
      return getFirestore(this.app);
    }
  }

  /**
   * Get the Firestore database ID from environment variables
   * Returns undefined if not specified, which uses the (default) database
   */
  private getDatabaseId(): string | undefined {
    const customDbId = import.meta.env['_NGX_ENV_FIREBASE_DATABASE_ID'];
    if (customDbId && customDbId.trim() !== '') {
      return customDbId.trim();
    }
    return undefined;
  }

  /**
   * Get the current database ID being used by this Firebase service instance
   * Returns undefined if using the default database
   */
  public getCurrentDatabaseId(): string | undefined {
    return this.getDatabaseId();
  }

  /**
   * Get a connection to the central (default) database for super admin operations
   * This always returns the default database regardless of Portal's database configuration
   * Use this for operations that need to see all clients across databases
   */
  public getCentralDatabase() {
    console.log('🏛️ Using central (default) database for super admin operations');
    return getFirestore(this.app); // Always default database, no databaseId parameter
  }

  /**
   * Get a Firestore database connection for a specific database ID
   * This is used for client-specific database routing in hybrid architectures
   * @param databaseId - The database ID to connect to (undefined = default database)
   * @returns Firestore instance for the specified database
   */
  public getDatabase(databaseId?: string) {
    if (!databaseId) {
      // Return default database
      return getFirestore(this.app);
    }
    
    console.log(`🔀 Connecting to client database: ${databaseId}`);
    return getFirestore(this.app, databaseId);
  }

  private connectToEmulators() {
    try {
      // Connect to Firebase emulators if in development
      console.log('🔧 Connecting to Firebase emulators...');
      
      // Auth emulator
      connectAuthEmulator(this.auth, 'http://localhost:9099');
      
      // Firestore emulator
      connectFirestoreEmulator(this.db, 'localhost', 8080);
      
      // Functions emulator
      connectFunctionsEmulator(this.functions, 'localhost', 5001);
      
      // Storage emulator
      connectStorageEmulator(this.storage, 'localhost', 9199);
      
      console.log('✅ Connected to Firebase emulators successfully!');
      console.log('📍 Emulator endpoints:');
      console.log('   • Auth: http://localhost:9099');
      console.log('   • Firestore: http://localhost:8080');
      console.log('   • Functions: http://localhost:5001');
      console.log('   • Storage: http://localhost:9199');
      
    } catch (error) {
      console.warn('⚠️ Could not connect to emulators (they may not be running):', error);
    }
  }
}
